<script setup lang="ts">
// #803: 作品一覧の器。トップ / `/arcanelab` / `/c/<slug>` / `/cradle` の 4 面が
// 別々に持っていた「タブ + ロード制御 + ページング」を 1 つにする。
//
// **並べる器（グリッド / カルーセル）は default slot で親に任せる**。
// centering43 の中は `calc(N * var(--u))`、landing は `dvw` という使い分けが
// あるため（`doc/architecture/responsive_sizing.md`）、グリッドまで統一すると
// レイアウト規約のほうを壊す。ここが引き受けるのは振る舞いと、共通化できる
// タブ・末尾ローダーだけ。
import type { Work, WorksSortKey } from '~/composables/api/useWorkApi'
import type { WorkGalleryFetcher, WorkGalleryTab } from '~/composables/useWorkGallery'

interface Props {
  fetcher: WorkGalleryFetcher
  perPage: number
  /** 省略 = タブなし（`/c` と `/cradle`）。 */
  tabs?: WorkGalleryTab[]
  tabVariant?: 'pill' | 'mobile'
  /** 現在のタブ。`v-model:sort` で親が持つ（面が desktop/mobile に分岐しても 1 つに保つため）。 */
  sort?: WorksSortKey
  /** SSR 等で親がすでに持っている 1 ページ目（`/c` の creator レスポンス同梱ぶん）。 */
  initialItems?: Work[]
  initialTotal?: number
  /** `fixed` = 1 ページで打ち止め（トップの 6 件固定）。 */
  mode?: 'append' | 'fixed'
  /** 全件出し切ったときに末尾へ出す文言。省略すると何も出さない。 */
  endText?: string
  /** タブ切替時に先頭へ戻すスクロールコンテナ。省略時は何もしない。 */
  scrollRoot?: HTMLElement | null
}

const props = withDefaults(defineProps<Props>(), {
  tabVariant: 'pill',
  sort: 'new',
  mode: 'append',
})

const emit = defineEmits<{
  'update:sort': [value: WorksSortKey]
  /** 初回ページを出し終えた（`usePageReady` の finishLoading 用）。 */
  'loaded': []
}>()

const gallery = useWorkGallery({
  fetcher: (page, perPage, sort) => props.fetcher(page, perPage, sort),
  perPage: props.perPage,
  initialSort: props.sort,
  initialItems: props.initialItems,
  initialTotal: props.initialTotal,
  mode: props.mode,
})

const {
  works, total, loading, loadingMore, hasMore, isEmpty, error,
  load, loadMore, changeSort, reload, retry,
} = gallery

// タブは親が持つ（controlled）。クリックは親へ上げ、親が prop を更新したら取り直す。
watch(() => props.sort, async (next) => {
  await changeSort(next)
  // 積み増したぶんスクロールした状態で別タブの短いリストに切り替わると空白だけが
  // 見えるので、器の先頭へ戻す。`scrollRoot` は親が持つ DOM 要素そのもの。
  const root: HTMLElement | null = props.scrollRoot ?? null
  if (root) root.scrollTop = 0
})

onMounted(async () => {
  await load()
  emit('loaded')
})

defineExpose({ reload, retry, works, total, error })
</script>

<template>
  <div class="work-gallery">
    <WorkGalleryTabs
      v-if="tabs?.length"
      :tabs="tabs"
      :model-value="sort"
      :variant="tabVariant"
      @update:model-value="emit('update:sort', $event)"
    />

    <slot
      v-if="loading && works.length === 0"
      name="loading"
    />

    <slot
      v-else-if="isEmpty"
      name="empty"
    />

    <template v-else>
      <slot
        :works="works"
        :total="total"
      />

      <WorkGalleryLoader
        v-if="mode === 'append'"
        :has-more="hasMore"
        :loading="loadingMore"
        :failed="Boolean(error)"
        :end-text="endText"
        @load="loadMore"
        @retry="retry"
      />
    </template>
  </div>
</template>
